import { useEffect, useRef, FC } from 'react'
import { t } from 'translations'

import { Input } from 'ui'

// Map of the wanted value for the specific field
const componentForm = {
  street_number: 'short_name', // street number
  route: 'long_name', // road/boulvard name
  locality: 'long_name', // city name
  administrative_area_level_1: 'short_name', // province/state
  country: 'long_name', // Country
  postal_code: 'short_name' // Postal code / zip code
}

type Props = {
  onSelect: (address: Partial<MData.Address>) => void
}

const SearchAddress: FC<Props> = ({ onSelect }) => {
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null)

  // Function to geolocate the user to make the search more accurate
  function geolocate() {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(function (position) {
        const geolocation = { lat: position.coords.latitude, lng: position.coords.longitude }
        const circle = new window.google.maps.Circle({ center: geolocation, radius: position.coords.accuracy })
        const circleBounds = circle.getBounds()
        if (circleBounds) {
          autocompleteRef.current?.setBounds(circleBounds)
        }
      })
    }
  }

  useEffect(() => {
    // when an address has been picked by the user, fill in the object
    async function fillInAddress() {
      const wantedFields = ['room', 'floor', 'street_number', 'route', 'locality', 'administrative_area_level_1', 'postal_code', 'country']
      // Get the place details from the autocomplete object.
      const place = autocompleteRef.current?.getPlace()
      if (!place) return ''
      // Adds the user's latitude and longitude
      const address: Partial<MData.Address> = {
        latitude: place?.geometry?.location?.lat(),
        longitude: place?.geometry?.location?.lng()
      }

      // Get each component of the address from the place details and fill the corresponding field on the form.
      const addressComponents = place?.address_components
      if (Array.isArray(addressComponents)) {
        for (let i = 0; i < addressComponents.length; i++) {
          const addressType = addressComponents[i].types[0]
          if (wantedFields.includes(addressType)) {
            address[addressType] = addressComponents[i][componentForm[addressType]]
          }
        }
      }

      onSelect(address)
    }

    // Init the auto completion
    function initAutocomplete() {
      geolocate()
      const element = document.getElementById('autocomplete') as HTMLInputElement
      // Create the autocomplete object, restricting the search to geographical location types.
      const autocomplete = new window.google.maps.places.Autocomplete(element, { types: ['geocode'] })
      autocompleteRef.current = autocomplete

      // When the user selects an address from the dropdown, populate the address fields in the form.
      autocomplete?.addListener('place_changed', fillInAddress)
    }

    initAutocomplete()
  }, [onSelect])

  return (
    <div className="form-group">
      <label className="control-label">{t('components.address.SEARCH_FOR_AN_ADDRESS')}</label>
      <Input id="autocomplete" autoComplete="off" placeholder={t('components.address.START_TAPPING_TO_SEARCH')} />
    </div>
  )
}

export default SearchAddress
